import React from 'react';
import { useNavigate } from 'react-router-dom';
import { QuizApiClient } from '@/infrastructure/quiz/QuizApiClient';
import { useAppTranslation } from '@/i18n/useAppTranslation';
import { getAbsoluteAppUrl } from '@/utils/appBase';
import { formatApiErrorMessage } from '@/utils/formatApiErrorMessage';

export default function QuizManagementPage({ session }) {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const api = React.useMemo(() => new QuizApiClient(), []);
  const token = session?.accessToken || null;
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  const load = React.useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const data = await api.listMyQuizzes(token);
      setItems(data?.items || []);
    } catch (e) {
      setError(formatApiErrorMessage(e?.message, t) || t('errors.loadQuizList'));
    } finally {
      setLoading(false);
    }
  }, [api, token, t]);

  React.useEffect(() => {
    load();
  }, [load]);

  if (!token) {
    return (
      <div className="mx-auto max-w-4xl rounded-2xl bg-white p-6 shadow-sm">
        <h1 className="font-headline text-2xl font-extrabold">{t('quizMgmt.needLoginTitle')}</h1>
        <p className="mt-2 text-sm text-rose-700">{t('quizMgmt.needLoginBody')}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-headline text-3xl font-extrabold">{t('quizMgmt.title')}</h1>
          <p className="text-sm text-slate-600">{t('quizMgmt.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/quizzes/create')}
          className="rounded-xl bg-cyan-700 px-4 py-2 text-sm font-bold text-white"
        >
          {t('quizMgmt.createBtn')}
        </button>
      </div>

      {error ? (
        <p className="rounded-xl bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">{error}</p>
      ) : null}

      <div className="rounded-[2rem] border border-slate-100 bg-white p-4 shadow-sm">
        {loading ? (
          <p className="p-4 text-sm text-slate-500">{t('quizMgmt.loading')}</p>
        ) : items.length === 0 ? (
          <p className="p-4 text-sm text-slate-500">{t('quizMgmt.empty')}</p>
        ) : (
          <div className="space-y-3">
            {items.map((quiz) => (
              <div key={quiz.id} className="rounded-xl border border-slate-100 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="font-headline text-xl font-bold">{quiz.title}</h3>
                    <p className="text-sm text-slate-500">{quiz.description || t('quizMgmt.noDesc')}</p>
                    <p className="mt-1 text-xs text-slate-400">
                      {t('quizMgmt.code')}: <strong>{quiz.share_code || 'N/A'}</strong> | {t('quizMgmt.questions')}: {quiz.question_count} |{' '}
                      {Number(quiz.is_public || 0) === 1 ? t('quizMgmt.public') : t('quizMgmt.private')}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {Number(quiz.is_public || 0) === 1 && quiz.share_code ? (
                      <button
                        type="button"
                        onClick={() => navigate(`/play/${quiz.share_code}`)}
                        className="rounded-lg bg-[#006c49] px-3 py-2 text-xs font-bold text-white"
                      >
                        {t('quizMgmt.play')}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => {
                        if (!quiz.share_code) return;
                        navigator.clipboard.writeText(getAbsoluteAppUrl(`/play/${quiz.share_code}`));
                      }}
                      className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-bold text-slate-700"
                    >
                      {t('quizMgmt.copyLink')}
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(`/quizzes/${quiz.id}/edit`)}
                      className="rounded-lg bg-cyan-700 px-3 py-2 text-xs font-bold text-white"
                    >
                      {t('quizMgmt.edit')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
